import { CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined, EditOutlined, EyeFilled, FilePdfOutlined, FileProtectOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Card, Col, Divider, Empty, Popconfirm, Row, Skeleton, Space, Tag } from "antd";
import { useEffect, useState } from "react";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { toast } from "react-toastify";
import { frenchDate } from "../../../../variables/functions";
import { LOGGED_USER } from "../../../../variables/constants";
import SDIModal from "../../../../components/ui/SDIModal";

export const AgreementsTab = ({ agrements, agreementLoading, tauxCommissions, validated, myAgreement, agreementDocument, setTCModalOpen, focusAgreementId }) => {
    const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
    const [activeAgreement, setActiveAgreement] = useState(focusAgreementId || agrements?.[0]?.agreementId);
    // Pour une compagnie : on n'utilise `myAgreement` que s'il s'agit d'un vrai agréement
    // (objet contenant une compagnie). `myAgreement` vaut "" tant que le courtier n'a pas
    // de convention avec cette compagnie → on le traite comme « pas d'agréement ».
    const currentAgreement = !isPool
        ? (myAgreement && myAgreement.compagnie ? myAgreement : null)
        : (agrements?.find(a => a.agreementId === activeAgreement) || agrements?.[0]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmLoading, setConfirmLoading] = useState(false);
    // setTCModalOpen vient du parent (DetailsCourtier) qui rend réellement le modal des taux.
    const [agreementModalOpen, setAgreementModalOpen] = useState(false);

    useEffect(() => {
        if (focusAgreementId) {
            setActiveAgreement(focusAgreementId);
        } else if (!activeAgreement && agrements?.[0]?.agreementId) {
            setActiveAgreement(agrements[0].agreementId);
        }
    }, [focusAgreementId, agrements, activeAgreement]);

    const handleValidateAgreement = () => {
        setConfirmLoading(true);
        axiosInstance
            .get(`/api/agreement/agence/${currentAgreement?.agreementId}`)
            .then((res) => {
                // console.log("resssss", res.data);
                setConfirmLoading(false);
                toast("Agréement validé avec succès!");
                window.location.reload();
            })
            .catch((err) => {
                console.log(err);
                toast("Erreur lors de la validation de l'agrement");
                setConfirmLoading(false);
            });
    }

    const handleRevokeAgreement = () => {
        setConfirmLoading(true);
        axiosInstance
            .get(`/api/agreement/agence/revoke/${currentAgreement?.agreementId}`)
            .then((res) => {
                setConfirmLoading(false);
                toast("Agréement révoké avec succès!");
                window.location.reload();
            })
            .catch((err) => {
                console.log(err);
                setConfirmLoading(false);
            });
    }

    // Aucun agréement valide à afficher : pour une compagnie, cela signifie que le courtier
    // n'a pas encore ajouté sa convention de collaboration avec elle. On affiche un message
    // explicite au lieu de planter ; dès que la convention est ajoutée, `currentAgreement`
    // devient défini et le contenu normal s'affiche automatiquement.
    if (!currentAgreement || !currentAgreement.compagnie) {
        return (
            <Card
                style={{
                    borderRadius: '12px',
                    border: '2px solid #d9d9d9',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}
            >
                <Empty
                    image={<FileProtectOutlined style={{ fontSize: '64px', color: '#bfbfbf' }} />}
                    imageStyle={{ height: 'auto', marginBottom: '16px' }}
                    description={
                        <Space direction="vertical" size={4}>
                            <span style={{ color: '#262626', fontSize: '16px', fontWeight: 600 }}>
                                {isPool
                                    ? "Aucun agréement disponible pour ce courtier."
                                    : "Ce courtier n'a pas encore ajouté sa convention de collaboration avec votre compagnie."}
                            </span>
                            {!isPool && (
                                <span style={{ color: '#8c8c8c', fontSize: '14px' }}>
                                    Dès qu'il aura ajouté sa convention, son agréement s'affichera automatiquement ici.
                                </span>
                            )}
                        </Space>
                    }
                    style={{ padding: '40px 16px' }}
                />
            </Card>
        );
    }

    return (
        <div>
            {/* Onglets des compagnies */}
            <div style={{ marginBottom: '20px' }}>
                <Space size="small" wrap>
                    {LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL" ? agrements.map((agr) => (
                        <Button
                            key={agr.agreementId}
                            size="large"
                            type={activeAgreement === agr.agreementId ? 'primary' : 'default'}
                            icon={agr.validated ? <CheckCircleOutlined /> : <FileProtectOutlined />}
                            onClick={() => setActiveAgreement(agr.agreementId)}
                            style={{
                                borderRadius: '8px',
                                fontWeight: 600,
                                ...(activeAgreement === agr.agreementId ? {
                                    background: 'linear-gradient(135deg, #242166 0%, #1890ff 100%)',
                                    border: 'none'
                                } : {
                                    borderColor: agr.validated ? '#52c41a' : '#d9d9d9'
                                })
                            }}
                        >
                            Agréement - {agr.compagnie.code}
                        </Button>
                    )) : null}
                </Space>
            </div>

            {/* Contenu de l'agréement */}
            <Card
                style={{
                    borderRadius: '12px',
                    border: validated ? '2px solid #52c41a' : '2px solid #d9d9d9',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}
            >
                {/* En-tête avec info compagnie */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '24px',
                    padding: '16px',
                    background: validated
                        ? 'linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)'
                        : 'linear-gradient(135deg, #f0f5ff 0%, #d6e4ff 100%)',
                    borderRadius: '8px'
                }}>
                    <div>
                        <h3 style={{
                            margin: 0,
                            color: '#242166',
                            fontSize: '18px',
                            fontWeight: 700
                        }}>
                            {currentAgreement?.compagnie.raisonSociale}
                        </h3>
                        <p style={{
                            margin: '4px 0 0 0',
                            color: '#8c8c8c',
                            fontSize: '14px'
                        }}>
                            Code compagnie: <strong>{currentAgreement?.compagnie.code}</strong>
                        </p>
                    </div>
                    <div>
                        {validated ? (
                            <Tag
                                icon={<CheckCircleOutlined />}
                                color="success"
                                style={{
                                    fontSize: '14px',
                                    padding: '6px 16px',
                                    borderRadius: '6px',
                                    fontWeight: 600
                                }}
                            >
                                VALIDÉ - {frenchDate(currentAgreement?.updatedAt)}
                            </Tag>
                        ) : (
                            <Tag
                                icon={<CloseCircleOutlined />}
                                color="warning"
                                style={{
                                    fontSize: '14px',
                                    padding: '6px 16px',
                                    borderRadius: '6px',
                                    fontWeight: 600
                                }}
                            >
                                EN ATTENTE DE VALIDATION
                            </Tag>
                        )}
                    </div>
                </div>

                {/* Visualisation du document */}
                <div style={{
                    background: '#fafafa',
                    padding: '24px',
                    borderRadius: '8px',
                    marginBottom: '24px',
                    minHeight: '400px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {agreementLoading ? (
                        <Skeleton active paragraph={{ rows: 8 }} />
                    ) : (
                        <div style={{ textAlign: 'center' }}>
                            <FilePdfOutlined style={{
                                fontSize: '80px',
                                color: '#242166',
                                marginBottom: '16px'
                            }} />
                            <p style={{ color: '#595959', fontSize: '16px', fontWeight: 500 }}>
                                {currentAgreement?.document}
                            </p>
                            <p style={{ color: '#8c8c8c', fontSize: '14px' }}>
                                Document d'agréement au format PDF
                            </p>
                            <Button
                                type="default"
                                size="large"
                                icon={<EyeFilled />}
                                style={{
                                    marginTop: '16px',
                                    borderRadius: '8px',
                                    borderColor: '#242166',
                                    color: '#242166',
                                    fontWeight: 600
                                }}
                                onClick={() => setAgreementModalOpen(true)}
                            >
                                Prévisualiser le document
                            </Button>
                        </div>
                    )}
                </div>

                {/* Actions */}
                <Space size="middle" wrap>
                    <Popconfirm
                        title={validated ? "Révoquer l'agréement" : "Valider l'agréement"}
                        description={
                            validated
                                ? "Êtes-vous sûr de vouloir révoquer cet agréement ? Cette action est réversible."
                                : "Êtes-vous sûr de vouloir valider cet agréement ? Le courtier pourra alors produire des contrats."
                        }
                        open={confirmOpen}
                        onConfirm={validated ? handleRevokeAgreement : handleValidateAgreement}
                        onCancel={() => setConfirmOpen(false)}
                        okText="Oui"
                        cancelText="Non"
                        okButtonProps={{
                            loading: confirmLoading,
                            style: {
                                background: validated ? '#ff4d4f' : '#52c41a',
                                border: 'none',
                                borderRadius: '6px'
                            }
                        }}
                        cancelButtonProps={{
                            style: { borderRadius: '6px' }
                        }}
                    >
                        <Button
                            type="primary"
                            size="large"
                            icon={validated ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
                            loading={agreementLoading}
                            onClick={() => setConfirmOpen(true)}
                            style={{
                                background: validated
                                    ? '#ff4d4f'
                                    : 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: 600,
                                minWidth: '140px'
                            }}
                        >
                            {validated ? 'Révoquer' : 'Valider'}
                        </Button>
                    </Popconfirm>

                    {/* <Button
                        size="large"
                        icon={<UploadOutlined />}
                        style={{
                            borderRadius: '8px',
                            borderColor: '#242166',
                            color: '#242166',
                            fontWeight: 600
                        }}
                    >
                        Remplacer le document
                    </Button> */}
                </Space>

                {/* Section Taux de commission (visible uniquement si validé) */}
                {validated && (
                    <>
                        <Divider style={{ margin: '32px 0' }} />

                        <div style={{
                            padding: '20px',
                            background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)',
                            borderRadius: '8px',
                            marginBottom: '16px'
                        }}>
                            <Row align="middle" justify="space-between">
                                <Col>
                                    <Space direction="vertical" size={0}>
                                        <h4 style={{
                                            margin: 0,
                                            color: '#242166',
                                            fontSize: '16px',
                                            fontWeight: 700
                                        }}>
                                            Taux de commission par catégorie
                                        </h4>
                                        <p style={{
                                            margin: '4px 0 0 0',
                                            color: '#8c8c8c',
                                            fontSize: '13px'
                                        }}>
                                            Définissez les taux de commission applicables pour ce courtier
                                        </p>
                                    </Space>
                                </Col>
                                <Col>
                                    <Button
                                        type="primary"
                                        size="large"
                                        icon={<EditOutlined />}
                                        onClick={() => setTCModalOpen(true)}
                                        style={{
                                            background: 'linear-gradient(135deg, #242166 0%, #1890ff 100%)',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontWeight: 600
                                        }}
                                    >
                                        Gérer les taux
                                    </Button>
                                </Col>
                            </Row>
                        </div>

                        <Row gutter={[12, 12]}>
                            {tauxCommissions.map((tc) => (
                                <Col xs={24} sm={12} md={6} key={tc.key}>
                                    <Card
                                        size="small"
                                        style={{
                                            borderRadius: '8px',
                                            border: '2px solid #242166',
                                            textAlign: 'center'
                                        }}
                                        bodyStyle={{ padding: '16px' }}
                                    >
                                        <div style={{
                                            fontSize: '28px',
                                            fontWeight: 700,
                                            color: '#242166',
                                            marginBottom: '4px'
                                        }}>
                                            {tc.taux}%
                                        </div>
                                        <div style={{
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            color: '#262626',
                                            marginBottom: '4px'
                                        }}>
                                            {tc.categorie?.startsWith('T') ? `TPV - ${tc.codeCategorie === "A" ? "Taxi" : "Bus"}` : tc.categorie}
                                        </div>
                                        <Tag
                                            color="#242166"
                                            style={{
                                                fontSize: '11px',
                                                borderRadius: '4px',
                                                margin: 0
                                            }}
                                        >
                                            {tc.codeCategorie}
                                        </Tag>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </>
                )}
            </Card>
            <SDIModal
                open={agreementModalOpen}
                onCancel={() => setAgreementModalOpen(false)}
                onOk={() => setAgreementModalOpen(false)}
            >
                {agreementLoading || !agreementDocument ? <Skeleton /> : currentAgreement?.document?.split(".")[1] === "pdf" ?
                    <embed
                        src={agreementDocument}
                        type="application/pdf"
                        style={{
                            width: "100%",
                            height: "1000px",
                        }}
                    /> :
                    <img
                        class="img-fluid rounded mb-4"
                        src={agreementDocument}
                        height="1000"
                        width="1000"
                        alt="Agreement"
                    />}
            </SDIModal>
        </div>
    );
};
